/* eslint-disable import/no-unresolved */
/* eslint-disable import/extensions */
import React, { ReactElement, SyntheticEvent } from "react";
import styled from "styled-components";

import StyledSelect from "../../../reusable/styledSelect";

import {
  black,
  red,
  red_lightest,
} from "../../../../helperFunctions/styledComponents";

type Option = {
  label: string;
  value: string;
  active?: boolean;
};

interface LevelProps {
  options: Option[];
  defaultOptions: Option[];
  handleChange: (values: Option[] | Option, position: number) => void;
  handleInputChange: (
    selectedValues: Option[],
    position: number,
    action: object
  ) => void;
  handleBlur: (event: SyntheticEvent<HTMLElement>, name: string) => void;
  selectedOptions: Option[];
  position: number;
  totalLevels: number;
  errors: {
    name?: Array<string>;
  };
  disabled: boolean;
  handleRemoveLevel: (number) => void;
}

const optionStyles = {
  multiValue: (styles, { data }) => {
    return {
      ...styles,
      border: data.active ? "initial" : `1px solid ${red}`,
    };
  },
  multiValueLabel: (styles, { data }) => {
    return {
      ...styles,
      color: data.active ? "initial" : `${red}`,
    };
  },
  multiValueRemove: (styles, { data }) => {
    return {
      ...styles,
      color: data.active ? "initial" : `${red}`,
    };
  },
};

const StyledFieldset = styled.fieldset`
  border: none;
  border-top: 1px solid ${black};
  margin-top: 5px;
  position: relative;
`;

const StyledSelectWrapper = styled.div`
  position: relative;
`;

const StyledLegend = styled.legend`
  padding: 5px 10px;
  margin: 0 0 15px 20px;
  background-color: white;
  text-transform: uppercase;
  font-size: 12px;
  border: none;
  width: auto;
`;

const StyledError = styled.p`
  color: ${red};
  margin: 5px 0 !important;
`;

StyledError.displayName = "StyledError"; // set displayName for Enzyme selecting

const StyledCloseButton = styled.button`
  color: currentColor !important;
  margin: 0 0 0 5px !important;
  box-shadow: none;
  background: none;
  position: absolute;
  right: -40px;
  top: 4px;

  &:hover {
    color: ${red} !important;
    box-shadow: none;
    background: #ffbdad;
  }
`;

StyledCloseButton.displayName = "StyledCloseButton";

const Level = ({
  options,
  defaultOptions,
  handleChange,
  handleInputChange,
  selectedOptions,
  position,
  totalLevels,
  errors,
  handleBlur,
  disabled,
  handleRemoveLevel,
}: LevelProps): ReactElement => {
  const name = `level_${position}`;
  const getPositionText = (pos: number): string => {
    switch (pos) {
      case 1:
        return " first";
      case totalLevels:
        return " last";
      default:
        return " next";
    }
  };

  const positionText = getPositionText(position);
  return (
    <StyledFieldset>
      <StyledLegend>
        <b>
          Level {position} reviews {positionText}
        </b>
      </StyledLegend>
      <StyledSelectWrapper>
        <StyledSelect
          isMulti
          isClearable={false}
          options={options}
          styles={optionStyles}
          defaultOptions={defaultOptions}
          handleChange={(values): void => {
            return handleChange(values, position);
          }}
          handleInputChange={(_, action): void => {
            return handleInputChange(selectedOptions, position, action);
          }}
          name={name}
          disabled={disabled}
          selectedOptions={selectedOptions}
          handleBlur={(e): void => {
            return handleBlur(e, name);
          }} // need to pass in name because of react-select issue: https://github.com/JedWatson/react-select/issues/3523
        />
        {totalLevels > 1 ? (
          <StyledCloseButton
            type="button"
            className="btn btn-link"
            onClick={(): void => {
              return handleRemoveLevel(position);
            }}
          >
            <i aria-hidden="true" className="fas fa-times fa-sm" />
          </StyledCloseButton>
        ) : null}
      </StyledSelectWrapper>
      <StyledError>
        <em>
          <small>
            {errors[name] && errors[name].length
              ? errors[name].join(", ").replace(/, ([^,]*)$/, " and $1")
              : null}
          </small>
        </em>
      </StyledError>
    </StyledFieldset>
  );
};

export default Level;
